import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { adminService } from "../../services/adminService";
import toast from "react-hot-toast";
import ImageUploadField from "../../components/Common/ImageUploadField";
import { IMAGE_SPECS } from "../../utils/imageSpecs";
import "./SpecialScreeningBulkBooking.css";

// ── Quill editor modules (used for hero / premium / bulk-booking copy) ──
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    [{ align: [] }],
    [{ color: [] }, { background: [] }],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
  "align",
  "color",
  "background",
];

const PAGE_KEY = "special-screening-bulk-booking";
// Folder used for uploads from this page. Must match the allow-list in
// the admin uploadController.ts.
const UPLOAD_FOLDER = "special-screening-bulk-booking";

// ── Types ──────────────────────────────────────────────────────────────────
interface PageConfig {
  id: string;
  page_key: string;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  meta_title: string | null;
  meta_description: string | null;
  is_active: boolean;
  sections: PageSection[];
}

interface PageSection {
  id: string;
  page_config_id: string;
  slug: string;
  title: string;
  display_order: number;
  is_active: boolean;
  data: any;
}

interface IconTitleItem {
  image_url?: string;
  icon?: string;
  title: string;
  description?: string;
}

const blankItem = (): IconTitleItem => ({ image_url: "", icon: "", title: "", description: "" });

// ── Component ─────────────────────────────────────────────────────────────
const SpecialScreeningBulkBooking: React.FC = () => {
  const [, setConfig] = useState<PageConfig | null>(null);
  const [sections, setSections] = useState<PageSection[]>([]);
  const [loading, setLoading] = useState(true);

  // Hero / page-level state
  const [heroTitle, setHeroTitle] = useState(
    "Special Screening & Bulk Booking",
  );
  const [heroSubtitle, setHeroSubtitle] = useState("Events");
  const [heroDescription, setHeroDescription] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);

  // Section state — full_banner
  const [bannerImage, setBannerImage] = useState("");
  const [savingBanner, setSavingBanner] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  const [wdTag, setWdTag] = useState("Special Screenings");
  const [wdHeading, setWdHeading] = useState(
    "Celebrate Every Occasion on the Big Screen",
  );
  const [wdSubHeading, setWdSubHeading] = useState(
    "Turn ordinary moments into unforgettable memories with exclusive private screenings at MYCINEMAS.",
  );
  const [wdItems, setWdItems] = useState<IconTitleItem[]>(
    Array.from({ length: 8 }, blankItem),
  );
  const [savingWd, setSavingWd] = useState(false);
  const [uploadingWdIcon, setUploadingWdIcon] = useState<number | null>(null);

  const [ppBg, setPpBg] = useState("");
  const [ppTag, setPpTag] = useState("Premium Package");
  const [ppContent, setPpContent] = useState(
    "<p>Enjoy a premium cinema experience with customizable packages, food & beverage options, and dedicated support.</p>",
  );
  const [ppButtonText, setPpButtonText] = useState(
    "Book Your Private Screening Today",
  );
  const [ppWhatsappNumber, setPpWhatsappNumber] = useState("");
  const [ppWhatsappText, setPpWhatsappText] = useState(
    "I would like to know more about the Private Screening package.",
  );
  const [savingPp, setSavingPp] = useState(false);
  const [uploadingPpBg, setUploadingPpBg] = useState(false);

  const [bbTag, setBbTag] = useState("BULK BOOKING");
  const [bbHeading, setBbHeading] = useState(
    "Book More. Save More. Entertain Better.",
  );
  const [bbDescription, setBbDescription] = useState(
    "<p>Looking for group movie bookings? MYCINEMAS offers hassle-free bulk ticket reservations for organizations, schools, colleges, corporates, clubs, and tour groups.</p>",
  );
  const [bbSideImage, setBbSideImage] = useState("");
  const [savingBb, setSavingBb] = useState(false);
  const [uploadingBbImage, setUploadingBbImage] = useState(false);

  const [igHeading, setIgHeading] = useState("Ideal For");
  const [igItems, setIgItems] = useState<IconTitleItem[]>(
    Array.from({ length: 6 }, blankItem),
  );
  const [savingIg, setSavingIg] = useState(false);
  const [uploadingIgIcon, setUploadingIgIcon] = useState<number | null>(null);

  const [bnHeading, setBnHeading] = useState("Benefits");
  const [bnDisclaimer, setBnDisclaimer] = useState("Terms & conditions apply.");
  const [bnItems, setBnItems] = useState<IconTitleItem[]>(
    Array.from({ length: 5 }, blankItem),
  );
  const [savingBn, setSavingBn] = useState(false);
  const [uploadingBnIcon, setUploadingBnIcon] = useState<number | null>(null);

  // Generic "create-or-update" helper for any section_type
  const upsertSection = async (
    sectionType: string,
    title: string,
    slug: string,
    displayOrder: number,
    dataPayload: any,
  ) => {
    const existing = sections.find((s) => s.data?.section_type === sectionType);
    const sectionData = {
      title,
      slug,
      displayOrder,
      isActive: true,
      data: { section_type: sectionType, ...dataPayload },
    };
    if (existing) {
      return adminService.updatePageSection(PAGE_KEY, existing.id, sectionData);
    }
    return adminService.createPageSection(PAGE_KEY, sectionData);
  };

  // ── Initial load ────────────────────────────────────────────────────────
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await adminService.getPageConfigByKey(PAGE_KEY);
      if (response.success && response.data) {
        const data: PageConfig = response.data;
        setConfig(data);
        setSections(data.sections || []);

        // Page-level
        setHeroTitle(data.title || "Special Screening & Bulk Booking");
        setHeroSubtitle(data.subtitle || "Events");
        setHeroDescription(data.description || "");
        setMetaTitle(data.meta_title || "");
        setMetaDescription(data.meta_description || "");
        setIsActive(Boolean(data.is_active));

        const lookup = (type: string) =>
          (data.sections || []).find((s: any) => s.data?.section_type === type);

        // full_banner
        const banner = lookup("full_banner");
        if (banner) setBannerImage(banner.data?.image_url || "");

        // what_we_do
        const wd = lookup("what_we_do");
        if (wd) {
          setWdTag(wd.data?.tag || "Special Screenings");
          setWdHeading(
            wd.data?.heading || "Celebrate Every Occasion on the Big Screen",
          );
          setWdSubHeading(
            wd.data?.sub_heading ||
              "Turn ordinary moments into unforgettable memories with exclusive private screenings at MYCINEMAS.",
          );
          if (Array.isArray(wd.data?.items) && wd.data.items.length > 0) {
            // Pad/truncate to exactly 8 slots, normalising image_url/description
            const incoming: IconTitleItem[] = wd.data.items;
            const padded = Array.from(
              { length: 8 },
              (_, i) => incoming[i] || blankItem(),
            ).map((it) => ({
              image_url: it.image_url || it.icon || "",
              icon: it.image_url || it.icon || "",
              title: it.title || "",
              description: it.description || "",
            }));
            setWdItems(padded);
          }
        }

        // premium_package
        const pp = lookup("premium_package");
        if (pp) {
          setPpBg(pp.data?.background_image_url || "");
          setPpTag(pp.data?.tag || "Premium Package");
          setPpContent(
            pp.data?.content ||
              "<p>Enjoy a premium cinema experience with customizable packages, food & beverage options, and dedicated support.</p>",
          );
          setPpButtonText(
            pp.data?.button_text || "Book Your Private Screening Today",
          );
          // Prefer the new WhatsApp fields. Fall back to the legacy single
          // button_link (as the number) so existing data isn't lost.
          setPpWhatsappNumber(
            pp.data?.whatsapp_number || pp.data?.button_link || "",
          );
          setPpWhatsappText(
            pp.data?.whatsapp_prefilled_text ||
              "I would like to know more about the Private Screening package.",
          );
        }

        // bulk_booking_intro
        const bb = lookup("bulk_booking_intro");
        if (bb) {
          setBbTag(bb.data?.tag || "BULK BOOKING");
          setBbHeading(
            bb.data?.heading || "Book More. Save More. Entertain Better.",
          );
          setBbDescription(
            bb.data?.description ||
              "<p>Looking for group movie bookings? MYCINEMAS offers hassle-free bulk ticket reservations for organizations, schools, colleges, corporates, clubs, and tour groups.</p>",
          );
          setBbSideImage(bb.data?.side_image_url || "");
        }

        // icon_grid
        const ig = lookup("icon_grid");
        if (ig) {
          setIgHeading(ig.data?.heading || "Ideal For");
          if (Array.isArray(ig.data?.items) && ig.data.items.length > 0) {
            const incoming: IconTitleItem[] = ig.data.items;
            const padded = Array.from(
              { length: 6 },
              (_, i) => incoming[i] || blankItem(),
            ).map((it) => ({
              image_url: it.image_url || it.icon || "",
              icon: it.image_url || it.icon || "",
              title: it.title || "",
              description: it.description || "",
            }));
            setIgItems(padded);
          }
        }

        // benefit_list
        const bn = lookup("benefit_list");
        if (bn) {
          setBnHeading(bn.data?.heading || "Benefits");
          setBnDisclaimer(bn.data?.disclaimer || "Terms & conditions apply.");
          if (Array.isArray(bn.data?.items) && bn.data.items.length > 0) {
            const incoming: IconTitleItem[] = bn.data.items;
            const padded = Array.from(
              { length: 5 },
              (_, i) => incoming[i] || blankItem(),
            );
            setBnItems(padded);
          }
        }
      }
    } catch (error: any) {
      console.error("Error loading page config:", error);
      toast.error("Failed to load page configuration");
    } finally {
      setLoading(false);
    }
  };

  const refreshPage = async () => {
    const response = await adminService.getPageConfigByKey(PAGE_KEY);
    if (response.success && response.data) {
      setConfig(response.data);
      setSections(response.data.sections || []);
    }
  };

  // ── Page-level (hero) save ─────────────────────────────────────────────
  const handleSaveHero = async () => {
    try {
      setSavingConfig(true);
      const response = await adminService.updatePageConfig(PAGE_KEY, {
        title: heroTitle,
        subtitle: heroSubtitle,
        description: heroDescription,
        metaTitle,
        metaDescription,
        isActive,
      });
      if (response.success) {
        toast.success("Page configuration saved");
        refreshPage();
      } else {
        toast.error(response.message || "Failed to save page configuration");
      }
    } catch (error: any) {
      console.error("Error saving hero config:", error);
      toast.error("Failed to save page configuration");
    } finally {
      setSavingConfig(false);
    }
  };

  // ── Generic image upload (returns URL string or null) ──────────────────
  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      const response = await adminService.uploadPageAsset(file, UPLOAD_FOLDER);
      if (response.success && response.data) {
        return response.data.url || response.data.filename || null;
      }
      toast.error("Upload failed");
    } catch (error: any) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
    }
    return null;
  };

  const uploadBannerImage = async (file: File): Promise<string | null> => {
    setUploadingBanner(true);
    try {
      const url = await uploadImage(file);
      if (!url) return null;
      setBannerImage(url);
      const existing = sections.find(
        (s) => s.data?.section_type === "full_banner",
      );
      const payload = {
        title: "Full Width Banner",
        slug: "full-banner",
        displayOrder: 1,
        isActive: true,
        data: { section_type: "full_banner", image_url: url },
      };
      const saveResp = existing
        ? await adminService.updatePageSection(PAGE_KEY, existing.id, payload)
        : await adminService.createPageSection(PAGE_KEY, payload);
      if (saveResp.success) {
        toast.success("Banner image uploaded & saved");
        refreshPage();
        return url;
      }
      return null;
    } finally {
      setUploadingBanner(false);
    }
  };

  const uploadWdIcon = async (
    file: File,
    index: number,
  ): Promise<string | null> => {
    setUploadingWdIcon(index);
    try {
      const url = await uploadImage(file);
      if (!url) return null;
      const updated = wdItems.map((it, i) =>
        i === index ? { ...it, image_url: url, icon: url } : it,
      );
      setWdItems(updated);
      const existing = sections.find(
        (s) => s.data?.section_type === "what_we_do",
      );
      const cleaned = updated
        .filter((i) => i.title.trim() !== "" || (i.image_url || "").trim() !== "")
        .map((i) => ({
          image_url: i.image_url || "",
          title: i.title,
          description: i.description || "",
        }));
      const payload = {
        title: "Special Screenings",
        slug: "special-screenings",
        displayOrder: 2,
        isActive: true,
        data: {
          section_type: "what_we_do",
          tag: wdTag,
          heading: wdHeading,
          sub_heading: wdSubHeading,
          items: cleaned,
        },
      };
      const saveResp = existing
        ? await adminService.updatePageSection(PAGE_KEY, existing.id, payload)
        : await adminService.createPageSection(PAGE_KEY, payload);
      if (saveResp.success) {
        toast.success("Image uploaded & saved");
        refreshPage();
        return url;
      }
      return null;
    } finally {
      setUploadingWdIcon(null);
    }
  };

  const uploadPpBg = async (file: File): Promise<string | null> => {
    setUploadingPpBg(true);
    try {
      const url = await uploadImage(file);
      if (!url) return null;
      setPpBg(url);
      const existing = sections.find(
        (s) => s.data?.section_type === "premium_package",
      );
      const payload = {
        title: "Premium Package",
        slug: "premium-package",
        displayOrder: 3,
        isActive: true,
        data: {
          section_type: "premium_package",
          background_image_url: url,
          tag: ppTag,
          content: ppContent,
          button_text: ppButtonText,
          whatsapp_number: ppWhatsappNumber,
          whatsapp_prefilled_text: ppWhatsappText,
        },
      };
      const saveResp = existing
        ? await adminService.updatePageSection(PAGE_KEY, existing.id, payload)
        : await adminService.createPageSection(PAGE_KEY, payload);
      if (saveResp.success) {
        toast.success("Background uploaded & saved");
        refreshPage();
        return url;
      }
      return null;
    } finally {
      setUploadingPpBg(false);
    }
  };

  const uploadBbImage = async (file: File): Promise<string | null> => {
    setUploadingBbImage(true);
    try {
      const url = await uploadImage(file);
      if (!url) return null;
      setBbSideImage(url);
      const existing = sections.find(
        (s) => s.data?.section_type === "bulk_booking_intro",
      );
      const payload = {
        title: "Bulk Booking",
        slug: "bulk-booking",
        displayOrder: 4,
        isActive: true,
        data: {
          section_type: "bulk_booking_intro",
          tag: bbTag,
          heading: bbHeading,
          description: bbDescription,
          side_image_url: url,
        },
      };
      const saveResp = existing
        ? await adminService.updatePageSection(PAGE_KEY, existing.id, payload)
        : await adminService.createPageSection(PAGE_KEY, payload);
      if (saveResp.success) {
        toast.success("Image uploaded & saved");
        refreshPage();
        return url;
      }
      return null;
    } finally {
      setUploadingBbImage(false);
    }
  };

  const uploadIgIcon = async (
    file: File,
    index: number,
  ): Promise<string | null> => {
    setUploadingIgIcon(index);
    try {
      const url = await uploadImage(file);
      if (!url) return null;
      const updated = igItems.map((it, i) =>
        i === index ? { ...it, image_url: url, icon: url } : it,
      );
      setIgItems(updated);
      const existing = sections.find(
        (s) => s.data?.section_type === "icon_grid",
      );
      const cleaned = updated
        .filter((i) => i.title.trim() !== "" || (i.image_url || "").trim() !== "")
        .map((i) => ({
          image_url: i.image_url || "",
          title: i.title,
          description: i.description || "",
        }));
      const payload = {
        title: "Ideal For",
        slug: "ideal-for",
        displayOrder: 5,
        isActive: true,
        data: {
          section_type: "icon_grid",
          heading: igHeading,
          items: cleaned,
        },
      };
      const saveResp = existing
        ? await adminService.updatePageSection(PAGE_KEY, existing.id, payload)
        : await adminService.createPageSection(PAGE_KEY, payload);
      if (saveResp.success) {
        toast.success("Image uploaded & saved");
        refreshPage();
        return url;
      }
      return null;
    } finally {
      setUploadingIgIcon(null);
    }
  };

  const uploadBnIcon = async (
    file: File,
    index: number,
  ): Promise<string | null> => {
    setUploadingBnIcon(index);
    try {
      const url = await uploadImage(file);
      if (!url) return null;
      const updated = bnItems.map((it, i) =>
        i === index ? { ...it, icon: url } : it,
      );
      setBnItems(updated);
      const existing = sections.find(
        (s) => s.data?.section_type === "benefit_list",
      );
      const cleaned = updated.filter(
        (i) => i.title.trim() !== "" || (i.icon || "").trim() !== "",
      );
      const payload = {
        title: "Benefits",
        slug: "benefits",
        displayOrder: 6,
        isActive: true,
        data: {
          section_type: "benefit_list",
          heading: bnHeading,
          disclaimer: bnDisclaimer,
          items: cleaned,
        },
      };
      const saveResp = existing
        ? await adminService.updatePageSection(PAGE_KEY, existing.id, payload)
        : await adminService.createPageSection(PAGE_KEY, payload);
      if (saveResp.success) {
        toast.success("Icon uploaded & saved");
        refreshPage();
        return url;
      }
      return null;
    } finally {
      setUploadingBnIcon(null);
    }
  };

  // ── full_banner save ────────────────────────────────────────────────────
  const handleSaveBanner = async () => {
    try {
      setSavingBanner(true);
      const response = await upsertSection(
        "full_banner",
        "Full Width Banner",
        "full-banner",
        1,
        { image_url: bannerImage },
      );
      if (response.success) {
        toast.success("Banner saved");
        refreshPage();
      } else {
        toast.error(response.message || "Failed to save banner");
      }
    } catch (error: any) {
      console.error("Error saving banner:", error);
      toast.error("Failed to save banner");
    } finally {
      setSavingBanner(false);
    }
  };

  // ── what_we_do save ────────────────────────────────────────────────────
  const handleSaveWd = async () => {
    try {
      setSavingWd(true);
      const cleaned = wdItems
        .filter((i) => i.title.trim() !== "" || (i.image_url || "").trim() !== "")
        .map((i) => ({
          image_url: i.image_url || "",
          title: i.title,
          description: i.description || "",
        }));
      const response = await upsertSection(
        "what_we_do",
        "Special Screenings",
        "special-screenings",
        2,
        {
          tag: wdTag,
          heading: wdHeading,
          sub_heading: wdSubHeading,
          items: cleaned,
        },
      );
      if (response.success) {
        toast.success("Special Screenings saved");
        refreshPage();
      } else {
        toast.error(response.message || "Failed to save Special Screenings");
      }
    } catch (error: any) {
      console.error("Error saving Special Screenings:", error);
      toast.error("Failed to save Special Screenings");
    } finally {
      setSavingWd(false);
    }
  };

  // ── premium_package save ───────────────────────────────────────────────
  const handleSavePp = async () => {
    try {
      setSavingPp(true);
      const response = await upsertSection(
        "premium_package",
        "Premium Package",
        "premium-package",
        3,
        {
          background_image_url: ppBg,
          tag: ppTag,
          content: ppContent,
          button_text: ppButtonText,
          whatsapp_number: ppWhatsappNumber,
          whatsapp_prefilled_text: ppWhatsappText,
        },
      );
      if (response.success) {
        toast.success("Premium Package saved");
        refreshPage();
      } else {
        toast.error(response.message || "Failed to save Premium Package");
      }
    } catch (error: any) {
      console.error("Error saving Premium Package:", error);
      toast.error("Failed to save Premium Package");
    } finally {
      setSavingPp(false);
    }
  };

  // ── bulk_booking_intro save ────────────────────────────────────────────
  const handleSaveBb = async () => {
    try {
      setSavingBb(true);
      const response = await upsertSection(
        "bulk_booking_intro",
        "Bulk Booking",
        "bulk-booking",
        4,
        {
          tag: bbTag,
          heading: bbHeading,
          description: bbDescription,
          side_image_url: bbSideImage,
        },
      );
      if (response.success) {
        toast.success("Bulk Booking saved");
        refreshPage();
      } else {
        toast.error(response.message || "Failed to save Bulk Booking");
      }
    } catch (error: any) {
      console.error("Error saving Bulk Booking:", error);
      toast.error("Failed to save Bulk Booking");
    } finally {
      setSavingBb(false);
    }
  };

  // ── icon_grid (Ideal For) save ─────────────────────────────────────────
  const handleSaveIg = async () => {
    try {
      setSavingIg(true);
      const cleaned = igItems
        .filter((i) => i.title.trim() !== "" || (i.image_url || "").trim() !== "")
        .map((i) => ({
          image_url: i.image_url || "",
          title: i.title,
          description: i.description || "",
        }));
      const response = await upsertSection(
        "icon_grid",
        "Ideal For",
        "ideal-for",
        5,
        {
          heading: igHeading,
          items: cleaned,
        },
      );
      if (response.success) {
        toast.success("Ideal For saved");
        refreshPage();
      } else {
        toast.error(response.message || "Failed to save Ideal For");
      }
    } catch (error: any) {
      console.error("Error saving Ideal For:", error);
      toast.error("Failed to save Ideal For");
    } finally {
      setSavingIg(false);
    }
  };

  // ── benefit_list save ──────────────────────────────────────────────────
  const handleSaveBn = async () => {
    try {
      setSavingBn(true);
      const cleaned = bnItems.filter(
        (i) => i.title.trim() !== "" || (i.icon || "").trim() !== "",
      );
      const response = await upsertSection(
        "benefit_list",
        "Benefits",
        "benefits",
        6,
        {
          heading: bnHeading,
          disclaimer: bnDisclaimer,
          items: cleaned,
        },
      );
      if (response.success) {
        toast.success("Benefits saved");
        refreshPage();
      } else {
        toast.error(response.message || "Failed to save Benefits");
      }
    } catch (error: any) {
      console.error("Error saving Benefits:", error);
      toast.error("Failed to save Benefits");
    } finally {
      setSavingBn(false);
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────
  const renderIconThumb = (icon: string) =>
    icon ? (
      <img src={icon} className="splb-icon-thumb" alt="" />
    ) : (
      <div className="splb-icon-thumb splb-icon-thumb--placeholder">
        <i className="bi bi-image"></i>
      </div>
    );

  // Generic items row renderer. `imageField` selects which field of the
  // IconTitleItem is the visual asset ("image_url" for the bg-image cards,
  // "icon" for the Benefits icons). `showDescription` adds a description
  // input under the title.
  const renderItemRow = (
    items: IconTitleItem[],
    setItems: React.Dispatch<React.SetStateAction<IconTitleItem[]>>,
    uploadingIndex: number | null,
    onUpload: (file: File, index: number) => Promise<string | null>,
    imageField: "image_url" | "icon" = "image_url",
    showDescription: boolean = false,
  ) =>
    items.map((it, idx) => {
      const imageValue = (it as any)[imageField] || "";
      return (
        <div className="splb-item-row" key={idx}>
          {renderIconThumb(imageValue)}
          <div className="flex-grow-1 d-flex flex-column gap-2">
            <Form.Control
              type="text"
              value={it.title}
              onChange={(e) => {
                const updated = items.map((x, i) =>
                  i === idx ? { ...x, title: e.target.value } : x,
                );
                setItems(updated);
              }}
              placeholder={`Item ${idx + 1} title`}
            />
            {showDescription && (
              <Form.Control
                as="textarea"
                rows={2}
                value={it.description || ""}
                onChange={(e) => {
                  const updated = items.map((x, i) =>
                    i === idx ? { ...x, description: e.target.value } : x,
                  );
                  setItems(updated);
                }}
                placeholder={`Item ${idx + 1} description (optional)`}
              />
            )}
          </div>
          <ImageUploadField
            spec={IMAGE_SPECS.specialScreeningTile}
            onUpload={(file) => onUpload(file, idx)}
            onChange={() => undefined}
            disabled={uploadingIndex === idx}
            buttonVariant="outline-secondary"
            size="sm"
            buttonLabel={uploadingIndex === idx ? "Uploading..." : (imageField === "image_url" ? "Image" : "Icon")}
            showPreview={false}
          />
        </div>
      );
    });

  // ── Loading state ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>Special Screening & Bulk Booking - Admin</title>
      </Helmet>

      <div className="container-fluid p-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Special Screening & Bulk Booking</h2>
            <p className="text-muted mb-0">
              Manage content, images, and sections for the public Special
              Screening & Bulk Booking page (Events dropdown).
            </p>
          </div>
        </div>

        {/* Sections table (read-only summary) */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-list-ul me-2"></i>
              Page Sections (in display order)
            </h5>
          </Card.Header>
          <Card.Body>
            <Table responsive striped hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>Slug</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Active</th>
                </tr>
              </thead>
              <tbody>
                {sections
                  .slice()
                  .sort((a, b) => a.display_order - b.display_order)
                  .map((s) => (
                    <tr key={s.id}>
                      <td>{s.display_order}</td>
                      <td>
                        <code>{s.slug}</code>
                      </td>
                      <td>{s.title}</td>
                      <td>
                        <span className="splb-section-type-badge">
                          {s.data?.section_type || "—"}
                        </span>
                      </td>
                      <td>
                        {s.is_active ? (
                          <span className="text-success">
                            <i className="bi bi-check-circle-fill"></i> Yes
                          </span>
                        ) : (
                          <span className="text-muted">
                            <i className="bi bi-dash-circle"></i> No
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                {sections.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-3">
                      No sections configured yet. Use the editors below to
                      create them — each will be auto-created on first save.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* ── 1) Page-level / Hero config ────────────────────────────── */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-house-door me-2"></i>
              Page Configuration (Hero)
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Page Title (hero h2)</Form.Label>
                  <Form.Control
                    type="text"
                    value={heroTitle}
                    onChange={(e) => setHeroTitle(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Subtitle (breadcrumb tail)</Form.Label>
                  <Form.Control
                    type="text"
                    value={heroSubtitle}
                    onChange={(e) => setHeroSubtitle(e.target.value)}
                    placeholder="Events"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Hero Description (rich text)</Form.Label>
              <ReactQuill
                theme="snow"
                modules={quillModules}
                formats={quillFormats}
                value={heroDescription}
                onChange={setHeroDescription}
                placeholder="Optional hero paragraph shown under the page title."
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Meta Title (SEO)</Form.Label>
                  <Form.Control
                    type="text"
                    value={metaTitle}
                    onChange={(e) => setMetaTitle(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Meta Description (SEO)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="splb-page-active"
                label="Page is active (visible on website)"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            </Form.Group>
            <Button
              variant="primary"
              onClick={handleSaveHero}
              disabled={savingConfig}
            >
              {savingConfig ? "Saving…" : "Save Page Configuration"}
            </Button>
          </Card.Body>
        </Card>

        {/* ── 2) Full-width banner ──────────────────────────────────── */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-image me-2"></i>
              Full-width Banner Image
            </h5>
          </Card.Header>
          <Card.Body>
            <div className="splb-preview-box">
              {IMAGE_SPECS.specialScreeningFullBanner.hint}
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Banner Image URL</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  value={bannerImage}
                  onChange={(e) => setBannerImage(e.target.value)}
                  placeholder="Upload or paste image URL"
                />
                <ImageUploadField
                  spec={IMAGE_SPECS.specialScreeningFullBanner}
                  onUpload={uploadBannerImage}
                  onChange={() => undefined}
                  disabled={uploadingBanner}
                  buttonVariant="outline-secondary"
                  buttonLabel={uploadingBanner ? "Uploading..." : "Upload"}
                  showPreview={false}
                />
              </InputGroup>
              {bannerImage && (
                <div className="mt-2">
                  <img
                    src={bannerImage}
                    alt="Banner preview"
                    style={{ maxWidth: 480, borderRadius: 8 }}
                  />
                </div>
              )}
            </Form.Group>
            <Button
              variant="primary"
              onClick={handleSaveBanner}
              disabled={savingBanner}
            >
              {savingBanner ? "Saving…" : "Save Banner"}
            </Button>
          </Card.Body>
        </Card>

        {/* ── 3) Special Screenings (What We Do) ─────────────────────── */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-stars me-2"></i>
              Special Screenings — 8 Cards
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Tag (small gold pill)</Form.Label>
                  <Form.Control
                    type="text"
                    value={wdTag}
                    onChange={(e) => setWdTag(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Heading</Form.Label>
                  <Form.Control
                    type="text"
                    value={wdHeading}
                    onChange={(e) => setWdHeading(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Sub-heading (supporting text)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={wdSubHeading}
                onChange={(e) => setWdSubHeading(e.target.value)}
              />
            </Form.Group>
            <Form.Label>Cards (background image + title + description)</Form.Label>
            {renderItemRow(
              wdItems,
              setWdItems,
              uploadingWdIcon,
              uploadWdIcon,
              "image_url",
              true,
            )}
            <div className="mt-3">
              <Button
                variant="primary"
                onClick={handleSaveWd}
                disabled={savingWd}
              >
                {savingWd ? "Saving…" : "Save Special Screenings"}
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* ── 4) Premium Package ─────────────────────────────────────── */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-award me-2"></i>
              Premium Package (gradient banner with button)
            </h5>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Background image URL</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  value={ppBg}
                  onChange={(e) => setPpBg(e.target.value)}
                  placeholder="Upload or paste image URL"
                />
                <ImageUploadField
                  spec={IMAGE_SPECS.specialScreeningPremiumBg}
                  onUpload={uploadPpBg}
                  onChange={() => undefined}
                  disabled={uploadingPpBg}
                  buttonVariant="outline-secondary"
                  buttonLabel={uploadingPpBg ? "Uploading..." : "Upload"}
                  showPreview={false}
                />
              </InputGroup>
              {ppBg && (
                <div className="mt-2">
                  <img
                    src={ppBg}
                    alt="Premium package preview"
                    style={{ maxWidth: 320, borderRadius: 8 }}
                  />
                </div>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Tag</Form.Label>
              <Form.Control
                type="text"
                value={ppTag}
                onChange={(e) => setPpTag(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Content (rich text)</Form.Label>
              <ReactQuill
                theme="snow"
                modules={quillModules}
                formats={quillFormats}
                value={ppContent}
                onChange={setPpContent}
              />
            </Form.Group>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Button text</Form.Label>
                  <Form.Control
                    type="text"
                    value={ppButtonText}
                    onChange={(e) => setPpButtonText(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Card className="mb-3 bg-light">
              <Card.Body>
                <h6 className="mb-3">
                  <i className="bi bi-whatsapp me-2 text-success"></i>
                  WhatsApp CTA (Premium Package button)
                </h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>WhatsApp Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={ppWhatsappNumber}
                        onChange={(e) =>
                          setPpWhatsappNumber(e.target.value)
                        }
                        placeholder="e.g., 918086876511"
                      />
                      <Form.Text className="text-muted">
                        Enter the WhatsApp number with country code (no + or
                        spaces). Leave empty to render a non-clickable button.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Prefilled Message</Form.Label>
                      <Form.Control
                        type="text"
                        value={ppWhatsappText}
                        onChange={(e) => setPpWhatsappText(e.target.value)}
                        placeholder="I would like to know more about the Private Screening package."
                      />
                      <Form.Text className="text-muted">
                        This message will be pre-filled when the user clicks
                        the button.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            <Button
              variant="primary"
              onClick={handleSavePp}
              disabled={savingPp}
            >
              {savingPp ? "Saving…" : "Save Premium Package"}
            </Button>
          </Card.Body>
        </Card>

        {/* ── 5) Bulk Booking intro ──────────────────────────────────── */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-people me-2"></i>
              Bulk Booking (left text + right image)
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Tag</Form.Label>
                  <Form.Control
                    type="text"
                    value={bbTag}
                    onChange={(e) => setBbTag(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Heading</Form.Label>
                  <Form.Control
                    type="text"
                    value={bbHeading}
                    onChange={(e) => setBbHeading(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Description (rich text)</Form.Label>
              <ReactQuill
                theme="snow"
                modules={quillModules}
                formats={quillFormats}
                value={bbDescription}
                onChange={setBbDescription}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Side image URL</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  value={bbSideImage}
                  onChange={(e) => setBbSideImage(e.target.value)}
                  placeholder="Upload or paste image URL"
                />
                <ImageUploadField
                  spec={IMAGE_SPECS.specialScreeningSideImage}
                  onUpload={uploadBbImage}
                  onChange={() => undefined}
                  disabled={uploadingBbImage}
                  buttonVariant="outline-secondary"
                  buttonLabel={uploadingBbImage ? "Uploading..." : "Upload"}
                  showPreview={false}
                />
              </InputGroup>
              {bbSideImage && (
                <div className="mt-2">
                  <img
                    src={bbSideImage}
                    alt="Bulk booking preview"
                    style={{ maxWidth: 320, borderRadius: 8 }}
                  />
                </div>
              )}
            </Form.Group>
            <Button
              variant="primary"
              onClick={handleSaveBb}
              disabled={savingBb}
            >
              {savingBb ? "Saving…" : "Save Bulk Booking"}
            </Button>
          </Card.Body>
        </Card>

        {/* ── 6) Ideal For (icon grid) ───────────────────────────────── */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-grid me-2"></i>
              Ideal For — 6 Cards
            </h5>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Section heading</Form.Label>
              <Form.Control
                type="text"
                value={igHeading}
                onChange={(e) => setIgHeading(e.target.value)}
              />
            </Form.Group>
            <Form.Label>Cards (background image + title + description)</Form.Label>
            {renderItemRow(
              igItems,
              setIgItems,
              uploadingIgIcon,
              uploadIgIcon,
              "image_url",
              true,
            )}
            <div className="mt-3">
              <Button
                variant="primary"
                onClick={handleSaveIg}
                disabled={savingIg}
              >
                {savingIg ? "Saving…" : "Save Ideal For"}
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* ── 7) Benefits (5 cards + disclaimer) ────────────────────── */}
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-check2-square me-2"></i>
              Benefits — 5 Cards
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Section heading</Form.Label>
                  <Form.Control
                    type="text"
                    value={bnHeading}
                    onChange={(e) => setBnHeading(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Disclaimer (e.g. "Terms & conditions apply.")
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={bnDisclaimer}
                    onChange={(e) => setBnDisclaimer(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Label>Cards (icon + title)</Form.Label>
            {renderItemRow(
              bnItems,
              setBnItems,
              uploadingBnIcon,
              uploadBnIcon,
              "icon",
              false,
            )}
            <div className="mt-3">
              <Button
                variant="primary"
                onClick={handleSaveBn}
                disabled={savingBn}
              >
                {savingBn ? "Saving…" : "Save Benefits"}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

export default SpecialScreeningBulkBooking;
